'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Church, Clock, Heart, MapPin, Users } from 'lucide-react';
import type { CollectionData, LocationGroup, MyServeResponse } from '@/lib/types';
import { campusSvgMap } from '@/lib/types';
import { fetchCollection, fetchLocations, fetchMyResponses } from '@/lib/api';
import {
  backFromSignIn,
  getMpWidgetToken,
  getSelectedCampusId,
  onCampusChange,
  onMpTokenChange,
  signInThenOpen,
} from '@/lib/campus';
import { campusLabel, fmtDay, parseMp } from '@/lib/format';
import { toPlainText } from '@/lib/richText';
import { useEventChrome } from '@/lib/useEventChrome';
import { WoodsideLogo } from '@church/nextjs-ui/website';
import { CampusIcon, CampusSvgProvider } from './CampusIcon';
import { PageSkeleton } from './OpportunityPage';
import { BandArt, KICKER, PageBand, accentTokens, isHexColor } from './PageBand';
import { ChecklistCard } from './ChecklistCard';
import { CollectionWho, type WhoPreset } from './CollectionWho';
import { CollectionDock } from './CollectionDock';

/**
 * A COLLECTION: one page for one effort, /serve/{slug}.
 *
 * "Thanksgiving Boxes" is an Opportunity with children (the ways to help)
 * and care offers (the ways to receive). The page is the event-details
 * layout: the Collection's band, the facts (all campuses, the ministry), the
 * Collection's checklist, and the Who card (CollectionWho), where the
 * household signs up to help or asks for a box in one sheet. The campus, and
 * who to ask there, are chosen in that sheet.
 *
 * `layout="sheet"` (data-layout="sheet") puts nothing on the page at all: the
 * host page's own buttons open the sheet. A link to #signup-serve opens it on
 * who is helping, #signup-receive on the box, and a page opened at either
 * hash opens with it. For a page that tells the story itself (Thanksgiving
 * Boxes keeps its own hero and copy) and only needs the sign-up.
 *
 * `layout="section"` (data-layout="section" on the embed) is the same page
 * without its band, as one section of someone else's page: the facts, the
 * checklist, the Who card and its sheets, in the column it was put in. No
 * phone dock, and nothing done to the page around it (header colour,
 * scrollbar, the hero's bleed).
 */

const CHURCH_WIDE = 1;

type Status = 'loading' | 'ready' | 'missing' | 'error';

export function CollectionPage({
  apiBaseUrl,
  reference,
  finderUrl,
  initialSide = 'help',
  layout = 'page',
}: {
  apiBaseUrl: string;
  /** Opportunity_ID or slug */
  reference: string | number;
  finderUrl: string;
  initialSide?: 'help' | 'care';
  /** 'page' takes the page over (band, header colour); 'section' sits in its column */
  layout?: 'page' | 'section' | 'sheet';
}) {
  const section = layout === 'section';
  const sheetOnly = layout === 'sheet';
  const [data, setData] = useState<CollectionData | null>(null);
  const [status, setStatus] = useState<Status>('loading');
  const [locations, setLocations] = useState<LocationGroup[]>([]);
  const [campus, setCampus] = useState<number | null>(() => getSelectedCampusId());
  const [mine, setMine] = useState<MyServeResponse[]>([]);
  // Opens the Who sheet from a role row, the phone dock, or a care link.
  const [preset, setPreset] = useState<WhoPreset | null>(null);
  const openWho = (p: Omit<WhoPreset, 'nonce'> = {}) => setPreset({ ...p, nonce: Date.now() });
  // The Who card, which the phone dock hands over to, and whether its sheet is open.
  const whoCard = useRef<HTMLElement>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    fetchCollection(apiBaseUrl, reference, controller.signal)
      .then((d) => {
        if (controller.signal.aborted) return;
        setData(d);
        setStatus(d ? 'ready' : 'missing');
      })
      .catch(() => !controller.signal.aborted && setStatus('error'));
    fetchLocations(apiBaseUrl, controller.signal)
      .then(setLocations)
      .catch(() => {});
    return () => controller.abort();
  }, [apiBaseUrl, reference]);

  useEffect(() => onCampusChange((id) => setCampus(id)), []);

  // Reached as the care side (a /care link into a Collection): open on the box page.
  useEffect(() => {
    if (data && initialSide === 'care' && data.careOffers.length) openWho({ page: 'box' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const refreshMine = useCallback(() => {
    void fetchMyResponses(apiBaseUrl).then((r) => setMine(r ?? []));
  }, [apiBaseUrl]);
  useEffect(() => {
    refreshMine();
    return onMpTokenChange(() => refreshMine());
  }, [refreshMine]);

  const svgMap = useMemo(() => campusSvgMap(locations), [locations]);
  const rootRef = useRef<HTMLDivElement>(null);
  useEventChrome(
    rootRef,
    data?.collection.accentColor,
    data?.collection.accentInk,
    status,
    layout === 'page'
  );

  // A page opened at #signup-serve / #signup-receive opens with the sheet
  // (coming back from sign-in lands here too), and in the sheet layout the
  // host page's own links to them open it. Serving needs a signed-in
  // household, so a signed-out tap on Serve goes straight to sign-in and
  // comes back to the sheet. A tap before the Collection has loaded waits.
  const [pendingSide, setPendingSide] = useState<'help' | 'care' | null>(null);
  useEffect(() => {
    const sideFor = (hash: string) =>
      hash === '#signup-serve' ? 'help' : hash === '#signup-receive' ? 'care' : null;
    // Serving needs a signed-in household: signed out, straight to sign-in,
    // never the sheet first. That holds for a page opened at the hash too,
    // which is also how a tap lands before this widget has loaded.
    const open = (side: 'help' | 'care') => {
      if (side === 'help' && !getMpWidgetToken() && !backFromSignIn())
        signInThenOpen('#signup-serve');
      else setPendingSide(side);
    };
    const first = sideFor(window.location.hash);
    if (first) open(first);
    if (!sheetOnly) return;
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)
        return;
      const a = (e.target as Element | null)?.closest?.('a[href*="#signup-"]');
      if (!(a instanceof HTMLAnchorElement)) return;
      const url = new URL(a.href, window.location.href);
      const side = sideFor(url.hash);
      // Another page's link goes there; that page opens the sheet itself.
      if (!side || url.pathname !== window.location.pathname) return;
      e.preventDefault();
      open(side);
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [sheetOnly]);
  useEffect(() => {
    if (!pendingSide || !data) return;
    openWho({ side: pendingSide });
    setPendingSide(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSide, data]);

  if (status !== 'ready' || !data) {
    if (sheetOnly) return null;
    if (section)
      return status === 'loading' ? (
        <ul
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
          aria-busy="true"
        >
          {[0, 1, 2, 3].map((i) => (
            <li key={i} className="h-36 animate-pulse rounded-2xl bg-gray-100" />
          ))}
        </ul>
      ) : (
        <p className="py-10 text-center text-base font-bold text-[#1c2b39]">
          {status === 'missing' ? "We couldn't find that." : "We couldn't load this right now."}
        </p>
      );
    return status === 'loading' ? (
      <PageSkeleton kind="collection" backHref={finderUrl} backLabel="All ways to serve" />
    ) : (
      <div className="flex flex-col items-center gap-3 rounded-2xl border border-gray-200 bg-white px-6 py-14 text-center">
        <p className="text-lg font-bold text-[#1c2b39]">
          {status === 'missing' ? "We couldn't find that." : "We couldn't load this right now."}
        </p>
        <a href={finderUrl} className="text-sm font-bold text-[#299c8f]">
          Find a place to serve
        </a>
      </div>
    );
  }

  const c = data.collection;
  const accent = isHexColor(c.accentColor) ? accentTokens(c.accentColor, c.accentInk) : null;
  const vars = accent
    ? ({
        '--accent': accent.base,
        '--on-accent': accent.onAccent,
        '--accent-text': accent.text,
        '--accent-deep': accent.deep,
        '--accent-soft': accent.soft,
        // The form kit's colours (the sheet's cards and buttons) follow the page.
        '--color-teal': accent.base,
        '--color-teal-deep': accent.deep,
        '--color-teal-soft': accent.soft,
      } as React.CSSProperties)
    : undefined;

  // Campuses this Collection reaches, from its roles and its care.
  const campusIds = [
    ...new Set(
      [
        ...data.children.map((o) => o.congregationId),
        ...data.careOffers.map((o) => o.congregationId),
      ].filter((id) => id !== CHURCH_WIDE)
    ),
  ];

  // One campus (Christmas Eve at Troy): name it, rather than "Church wide".
  const onlyCampus =
    campusIds.length === 1
      ? {
          id: campusIds[0],
          name: campusLabel(
            campusIds[0],
            data.children.find((o) => o.congregationId === campusIds[0])?.congregationName ??
              data.careOffers.find((o) => o.congregationId === campusIds[0])?.congregationName ??
              ''
          ),
        }
      : null;

  const dates = [...data.children.map((o) => o.date), ...data.careOffers.map((o) => o.date)]
    .filter((d): d is string => Boolean(d))
    .sort();
  const when =
    dates.length === 0
      ? 'Ongoing'
      : dates.length === 1 || fmtDay(parseMp(dates[0])) === fmtDay(parseMp(dates[dates.length - 1]))
        ? fmtDay(parseMp(dates[0]))
        : `${fmtDay(parseMp(dates[0]))} to ${fmtDay(parseMp(dates[dates.length - 1]))}`;
  const careType = data.careTypes?.[0] ?? null;
  const careLabel = careType
    ? (careType.wayInLabel ??
      `Receive ${/^[aeiou]/i.test(careType.name) ? 'an' : 'a'} ${careType.name}`)
    : null;

  // The page under the band: the facts, the checklist and the Who card. A
  // section is this alone; the page puts the band above it and the phone dock below.
  const body = (
    <div className="grid gap-7 lg:grid-cols-[minmax(0,1fr)_340px]">
      <div className="flex min-w-0 flex-col gap-4">
        <div className="divide-y divide-gray-200 rounded-2xl border border-gray-200 bg-white shadow-sm">
          {/* Church-wide, like an all-campus event: the campus is chosen in the sheet. */}
          <div className="grid grid-cols-[22px_1fr] gap-x-3 gap-y-1 px-5 py-4">
            <Church className="mt-px h-[18px] w-[18px] text-[var(--accent-text,var(--accent,#299c8f))]" />
            <span className={`${KICKER} text-[13px] text-gray-500`}>Campus</span>
            <div className="col-start-2 flex items-center gap-2.5 font-bold">
              {onlyCampus ? (
                <>
                  <CampusIcon congregationId={onlyCampus.id} className="h-7 w-7 shrink-0" />
                  {onlyCampus.name}
                </>
              ) : (
                <>
                  <WoodsideLogo
                    tone="current"
                    className="h-7 w-7 text-[var(--accent-text,var(--accent,#299c8f))]"
                  />
                  {campusIds.length > 1 ? 'All campuses' : 'Church wide'}
                </>
              )}
            </div>
          </div>
          {c.ministryName && (
            <div className="grid grid-cols-[22px_1fr] gap-x-3 gap-y-1 px-5 py-4">
              <Users className="mt-px h-[18px] w-[18px] text-[var(--accent-text,var(--accent,#299c8f))]" />
              <span className={`${KICKER} text-[13px] text-gray-500`}>Ministry</span>
              <span className="col-start-2 font-[family-name:var(--font-display)] text-[26px] leading-none tracking-[0.02em] uppercase">
                {c.ministryName}
              </span>
            </div>
          )}
        </div>

        {c.checklist && (
          <div className="mt-3">
            <ChecklistCard checklist={c.checklist} scope={`collection-${c.id}`} />
          </div>
        )}
      </div>

      <aside ref={whoCard} className="min-w-0" id="collection-who">
        <div
          data-dock-card
          className="flex flex-col gap-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm lg:sticky lg:top-24"
        >
          <CollectionWho
            apiBaseUrl={apiBaseUrl}
            collection={c}
            roles={data.children}
            defaultCampusId={campus}
            mine={mine}
            onSent={refreshMine}
            accentStyle={vars}
            preset={preset}
            careOffers={data.careOffers}
            careLabel={careLabel}
            onOpenChange={setSheetOpen}
          />
        </div>
      </aside>
    </div>
  );

  if (sheetOnly) {
    return (
      <CampusSvgProvider value={svgMap}>
        <div className="sf" style={vars}>
          <CollectionWho
            apiBaseUrl={apiBaseUrl}
            collection={c}
            roles={data.children}
            defaultCampusId={campus}
            mine={mine}
            onSent={refreshMine}
            accentStyle={vars}
            preset={preset}
            careOffers={data.careOffers}
            careLabel={careLabel}
            sheetOnly
          />
        </div>
      </CampusSvgProvider>
    );
  }

  if (section) {
    return (
      <CampusSvgProvider value={svgMap}>
        <div className="sf text-[#1c2b39]" style={vars}>
          {body}
        </div>
      </CampusSvgProvider>
    );
  }

  return (
    <CampusSvgProvider value={svgMap}>
      <div ref={rootRef} className="sf flex flex-col gap-6 text-[#1c2b39]" style={vars}>
        <PageBand
          art={<BandArt imageUrl={c.imageUrl} />}
          backHref={finderUrl}
          backLabel="All ways to serve"
          title={c.title}
          facts={[
            {
              icon: <Heart className="h-[18px] w-[18px]" />,
              label: 'What',
              value: <span className="line-clamp-3 font-medium">{toPlainText(c.description)}</span>,
            },
            { icon: <Clock className="h-[18px] w-[18px]" />, label: 'When', value: when },
            {
              icon: <MapPin className="h-[18px] w-[18px]" />,
              label: 'Where',
              value:
                campusIds.length === 0
                  ? 'Church wide'
                  : onlyCampus
                    ? onlyCampus.name
                    : `${campusIds.length} campuses`,
            },
          ]}
        />

        {body}

        {/* On a phone the Who card sits below everything: the event page's
            register bar brings it up, and steps down once the card is in view. */}
        {(data.children.length > 0 || data.careOffers.length > 0) && (
          <>
            <CollectionDock
              label="Get started"
              ways={[
                ...(data.children.length > 0 ? ['Volunteer'] : []),
                ...(careLabel && data.careOffers.length > 0 ? [careLabel] : []),
              ]}
              anchor={whoCard}
              hidden={sheetOpen}
              accentStyle={vars}
              onOpen={() => openWho()}
            />
            <div className="h-24 lg:hidden" aria-hidden="true" />
          </>
        )}
      </div>
    </CampusSvgProvider>
  );
}
